import type { VendorChunkedUploadInitResponse } from "../../types/vendor-chunked-upload"

/**
 * Default chunk size hint for preflight / tests only.
 * Runtime uploads MUST slice using `chunk_size_bytes` from the init response.
 */
export const VENDOR_STREAMING_UPLOAD_CHUNK_SIZE_BYTES = 4 * 1024 * 1024

export type StreamingUploadFileChunk = {
  index: number
  offset: number
  size: number
  blob: Blob
}

/** Handoff §7: expectedLength(i) = min(C, T - i * C) for 0-based index i. */
export const expectedChunkLength = (
  chunkIndex: number,
  chunkSizeBytes: number,
  totalBytes: number
): number => {
  if (chunkIndex < 0 || !Number.isFinite(chunkSizeBytes) || chunkSizeBytes <= 0) {
    throw new RangeError("chunkSizeBytes must be positive")
  }
  if (totalBytes < 0 || !Number.isFinite(totalBytes)) {
    throw new RangeError("totalBytes must be a non-negative finite number")
  }
  return Math.min(chunkSizeBytes, totalBytes - chunkIndex * chunkSizeBytes)
}

/**
 * Validates file size and that `total_chunks` matches the implied layout from C and T.
 */
export const assertFileMatchesInit = (
  file: File,
  init: Pick<
    VendorChunkedUploadInitResponse,
    "chunk_size_bytes" | "total_chunks" | "total_bytes"
  >
): void => {
  const { chunk_size_bytes: C, total_chunks: N, total_bytes: T } = init

  if (!Number.isFinite(C) || C <= 0) {
    throw new Error("Invalid chunk_size_bytes from upload session")
  }
  if (!Number.isFinite(T) || T <= 0) {
    throw new Error("Invalid total_bytes from upload session")
  }
  if (!Number.isInteger(N) || N < 1) {
    throw new Error("Invalid total_chunks from upload session")
  }
  if (file.size !== T) {
    throw new Error(
      `File size (${file.size} bytes) does not match upload session total_bytes (${T}).`
    )
  }

  const impliedChunks = Math.ceil(T / C)
  if (N !== impliedChunks) {
    throw new Error(
      `total_chunks (${N}) does not match expected layout (${impliedChunks}) for total_bytes ${T} and chunk_size_bytes ${C}.`
    )
  }
}

/**
 * Yields slices aligned with the server init response (use init's chunk_size_bytes, never a UI constant).
 */
export function* iterateVendorChunkedUploadSlices(
  file: File,
  init: Pick<
    VendorChunkedUploadInitResponse,
    "chunk_size_bytes" | "total_chunks" | "total_bytes"
  >
): Generator<StreamingUploadFileChunk> {
  assertFileMatchesInit(file, init)

  const C = init.chunk_size_bytes
  const T = init.total_bytes
  const N = init.total_chunks

  for (let i = 0; i < N; i++) {
    const size = expectedChunkLength(i, C, T)
    const offset = i * C
    yield {
      index: i,
      offset,
      size,
      blob: file.slice(offset, offset + size),
    }
  }
}

/** @deprecated Use iterateVendorChunkedUploadSlices with init metadata from POST /vendor/chunked-uploads */
export function* iterateStreamingUploadChunks(
  file: File,
  chunkSizeBytes: number = VENDOR_STREAMING_UPLOAD_CHUNK_SIZE_BYTES
): Generator<StreamingUploadFileChunk> {
  if (!Number.isFinite(chunkSizeBytes) || chunkSizeBytes <= 0) {
    throw new RangeError("chunkSizeBytes must be a finite positive number")
  }

  const total = file.size
  const N = Math.ceil(total / chunkSizeBytes) || 0
  if (N === 0) {
    return
  }

  yield* iterateVendorChunkedUploadSlices(file, {
    chunk_size_bytes: chunkSizeBytes,
    total_chunks: N,
    total_bytes: total,
  })
}

export const countStreamingUploadChunks = (
  fileSizeBytes: number,
  chunkSizeBytes: number = VENDOR_STREAMING_UPLOAD_CHUNK_SIZE_BYTES
): number => {
  if (fileSizeBytes <= 0) {
    return 0
  }
  return Math.ceil(fileSizeBytes / chunkSizeBytes)
}
